import { AnyID, Getter, SceneObjects, Setter } from '@types'
import {
  applyLiveDrawCameraFit,
  scheduleLiveDrawCameraFitRetries,
} from '@utils/camera/cameraControlsRegistry'
import { runWithoutHistoryRecordingAsync } from '@utils/historyRecording'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import getId from '@utils/sceneObjects/getId'
import { preloadSceneTextureUrls } from '@utils/sceneObjects/textureCache'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import type { DrawItemOptions } from './itemActions'

/**
 * Diagnóstico de dibujado. Activado por defecto para no depender de poner el flag
 * en consola tras cada recarga. Para silenciarlo: `window.__DRAW_DEBUG__ = false`.
 */
const isDrawDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  const flag = (window as Window & { __DRAW_DEBUG__?: boolean }).__DRAW_DEBUG__
  return flag !== false
}

const drawDebugExactOBB = (it: any): OBB => {
  const pos = new Vector3()
  const quat = new Quaternion()
  it.matrix.decompose(pos, quat, new Vector3())
  const center = new Vector3(it.width / 2, it.height / 2, it.thickness / 2)
    .applyQuaternion(quat)
    .add(pos)
  const rot = new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quat))
  return new OBB(center, new Vector3(it.width / 2, it.height / 2, it.thickness / 2), rot)
}

/** Rotación Y en grados de la matriz del item. */
const drawDebugRotYdeg = (it: any): number => {
  const quat = new Quaternion()
  it.matrix.decompose(new Vector3(), quat, new Vector3())
  const e = new Euler().setFromQuaternion(quat)
  return Math.round((e.y * 180) / Math.PI)
}

/** Huella en MUNDO (AABB en planta) del OBB: rangos X/Z reales que ocupa la unidad. */
const drawDebugWorldFootprint = (
  box: OBB,
): { xMin: number; xMax: number; zMin: number; zMax: number } => {
  const e = box.rotation.elements
  // half-extents proyectados a ejes de mundo: |R| · halfSize
  const hx = box.halfSize.x
  const hy = box.halfSize.y
  const hz = box.halfSize.z
  const worldHalfX = Math.abs(e[0]) * hx + Math.abs(e[3]) * hy + Math.abs(e[6]) * hz
  const worldHalfZ = Math.abs(e[2]) * hx + Math.abs(e[5]) * hy + Math.abs(e[8]) * hz
  const r1 = (n: number) => Math.round(n * 10) / 10
  return {
    xMin: r1(box.center.x - worldHalfX),
    xMax: r1(box.center.x + worldHalfX),
    zMin: r1(box.center.z - worldHalfZ),
    zMax: r1(box.center.z + worldHalfZ),
  }
}

/**
 * Loguea la posición de la unidad recién dibujada y detecta con qué otras unidades
 * se solapa (intersección de OBB exacto). Útil para ver "se dibuja una sobre otra".
 */
const logDrawOverlapDiagnostic = (ctx: DrawItemPostProcessingContext): void => {
  if (!isDrawDebugEnabled()) return
  const { get, item, pendingParentSnap } = ctx
  const store = get()
  const drawn = store.items.get(item.id as AnyID) as any
  if (!drawn || !drawn.matrix || !drawn.width) return
  // Solo unidades principales (no linkedSons).
  if (drawn.type === 'linkedSon') return

  const r1 = (n: number) => Math.round(n * 10) / 10
  const pos = new Vector3()
  drawn.matrix.decompose(pos, new Quaternion(), new Vector3())
  const drawnOBB = drawDebugExactOBB(drawn)

  const drawnFoot = drawDebugWorldFootprint(drawnOBB)

  const allUnits: any[] = []
  const overlaps: any[] = []
  store.items.forEach((other: any, oid: any) => {
    if (!other || (other.type !== 'items' && other.type !== 'linkedItem')) return
    if (!other.matrix || !other.width) return
    const op = new Vector3()
    other.matrix.decompose(op, new Quaternion(), new Vector3())
    const otherOBB = drawDebugExactOBB(other)
    const otherFoot = drawDebugWorldFootprint(otherOBB)
    allUnits.push({
      id: String(oid),
      catalog: other.catalogID,
      x: r1(op.x),
      z: r1(op.z),
      rotY: drawDebugRotYdeg(other),
      ancho: other.width,
      fondo: other.thickness,
      worldX: `${otherFoot.xMin}..${otherFoot.xMax}`,
      worldZ: `${otherFoot.zMin}..${otherFoot.zMax}`,
    })
    if (String(oid) === String(item.id)) return
    if (drawnOBB.intersectsOBB(otherOBB)) {
      // Penetración real en planta (cuánto se meten uno en otro). <=0 = solo se tocan.
      const penX =
        Math.min(drawnFoot.xMax, otherFoot.xMax) - Math.max(drawnFoot.xMin, otherFoot.xMin)
      const penZ =
        Math.min(drawnFoot.zMax, otherFoot.zMax) - Math.max(drawnFoot.zMin, otherFoot.zMin)
      overlaps.push({
        id: String(oid),
        catalog: other.catalogID,
        dist2D: r1(Math.hypot(op.x - pos.x, op.z - pos.z)),
        penetracionX: r1(penX),
        penetracionZ: r1(penZ),
        diagnostico:
          penX <= 1 || penZ <= 1 ? 'solo se tocan (borde)' : 'SOLAPE REAL (se meten uno en otro)',
      })
    }
  })

  // console.log('[DRAW] unidad dibujada', {
  //   id: String(item.id),
  //   catalog: drawn.catalogID,
  //   isKube: /^k\d/i.test(String(drawn.catalogID ?? '').trim()),
  //   colisionable: store.collisionableItems.has(item.id),
  //   pos: { x: r1(pos.x), z: r1(pos.z) },
  //   rotY: drawDebugRotYdeg(drawn),
  //   size: { ancho: drawn.width, fondo: drawn.thickness },
  //   huellaMundo: { X: `${drawnFoot.xMin}..${drawnFoot.xMax}`, Z: `${drawnFoot.zMin}..${drawnFoot.zMax}` },
  //   anclaSnap: pendingParentSnap ?? '(sin ancla → posición por defecto)',
  //   totalUnidades: allUnits.length,
  //   SOLAPA_CON: overlaps.length ? overlaps : 'ninguna ✅',
  // })
  if (overlaps.length) {
    // console.warn(
    //   `[DRAW] ⚠️ La unidad ${String(item.id)} (${drawn.catalogID}) SE SOLAPA con ${overlaps.length} unidad(es):`,
    //   overlaps,
    // )
  }
  // console.table(allUnits)
}

/**
 * Contexto compartido por todos los comportamientos post-dibujo de `drawItem`.
 * Reúne el estado calculado durante el dibujo para completar el comportamiento
 * específico de cada tipo de item una vez ya colocado en la escena.
 */
export type DrawItemPostProcessingContext = {
  get: Getter
  set: Setter
  item: any
  catalogID: string
  type: SceneObjects['type']
  backGLBInfo: any
  options?: DrawItemOptions
  pendingParentSnap: { anchorId: AnyID; snapSide: 'left' | 'right' } | null
  sourceCurrentObjectID: AnyID | ''
  preserveWorldTransform: boolean
  suppressCameraFit: boolean
}

/**
 * Reaplica los materiales de zona de un linkedItem al renderer sin disparar
 * múltiples incrementos del contador global (evita parpadeo negro en el visor).
 */
const pushLinkedItemZoneMaterialsToRenderer = (get: Getter, itemId: AnyID) => {
  const liveItem = get().items.get(itemId) as any
  if (!liveItem?.textures?.length) return
  liveItem.textures.forEach((tex: { zoneName?: string }) => {
    const zoneName = tex.zoneName
    const material = liveItem.zones?.[zoneName]?.material
    if (!zoneName || !material) return
    // skipCounterIncrement: evita N incrementos globales de contadorModificaciones
    // (provocan parpadeo negro en TODAS las unidades linkedItem del visor).
    get().editZone(
      itemId,
      zoneName,
      {
        ...material,
        color:
          typeof material.color === 'string' && material.color.trim().length > 0
            ? material.color
            : '#FFFFFF',
        url: material.url ? String(material.url).split('?')[0] : material.url,
      },
      true,
    )
  })
}

/**
 * linkedItem: reaplica el snap con la misma lógica usada en `collideItems`
 * para que la colocación final de `drawItem` coincida con ese flujo.
 */
const reapplyParentSnapAfterDraw = (ctx: DrawItemPostProcessingContext) => {
  const { get, set, item, pendingParentSnap, preserveWorldTransform } = ctx
  if (
    pendingParentSnap &&
    !item.universal &&
    item.type === 'linkedItem' &&
    !preserveWorldTransform
  ) {
    try {
      get().collideItems(item.id as AnyID, pendingParentSnap.anchorId as AnyID, {
        forcedSide: pendingParentSnap.snapSide,
      })

      // En frameloop=demand, forzar un repaint tras snap post-dibujo.
      set((s) => {
        s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
      })
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => r3f.invalidate?.())
          setTimeout(() => r3f.invalidate?.(), 40)
        }
      } catch {
        // noop
      }
    } catch (postSnapError) {
      console.warn('[drawItem] No se pudo aplicar post-snap OGadapted:', postSnapError)
    }
  }
}

/**
 * Items universales: aplica la textura/material guardado (herencia) a los nuevos
 * items cuando ya existen otros items universales en la escena.
 */
const scheduleUniversalTextureInheritance = (ctx: DrawItemPostProcessingContext) => {
  const { get, item } = ctx
  // APLICAR TEXTURA GUARDADA A NUEVOS ITEMS (HERENCIA)
  if (item.type === 'items' && item.universal && item.textures && item.textures.length > 0) {
    const storeAfterCreate = get()
    const savedMaterial = (storeAfterCreate as any).selectedEstructuraMaterial

    // Verificar si hay items existentes en escena (excluyendo el item que se acaba de crear)
    const existingItems = Array.from(storeAfterCreate.items.values()).filter(
      (existingItem) =>
        existingItem.type === 'items' && existingItem.id !== item.id && existingItem.universal,
    )

    // Solo aplicar herencia si hay items existentes Y hay material guardado
    if (savedMaterial && existingItems.length > 0) {
      // ATEMPORAL: priorizar zona "Tela" (fallback "Estructura" para compatibilidad)
      const inheritedTexture =
        item.textures.find((t: any) => t.zoneDisplayName === 'Tela') ??
        item.textures.find((t: any) => t.zoneDisplayName === 'Estructura')

      if (inheritedTexture && item.zones[inheritedTexture.zoneName]) {
        // Aplicar el material guardado usando editZone
        setTimeout(() => {
          const storeForEdit = get()
          const editZoneFn = storeForEdit.editZone
          if (editZoneFn) {
            const finalMaterial = { ...savedMaterial }
            // Asegurar que la URL esté limpia
            if (finalMaterial.url) {
              finalMaterial.url = finalMaterial.url.split('?')[0]
            }
            editZoneFn(item.id, inheritedTexture.zoneName, finalMaterial, true)
          }
        }, 100)
      }
    }
  }
}

function countDrawFitUnits(items: Map<string, any>): number {
  let n = 0
  for (const item of items.values()) {
    const t = String(item?.type || '')
    if (t !== 'linkedItem' && t !== 'items') continue
    if (!item?.matrix || item.visible === false) continue
    if (!(item.width > 0) || !(item.height > 0)) continue
    n++
  }
  return n
}

/**
 * Encuadre solo con la primera unidad dibujada.
 * A partir de la segunda, la cámara no se mueve.
 */
const scheduleCameraFit = (ctx: DrawItemPostProcessingContext) => {
  const { get, type, suppressCameraFit } = ctx
  if (suppressCameraFit || type === 'linkedSon') return

  // El item ya está en el store: 1 = primera → fit; >1 = no reposicionar.
  if (countDrawFitUnits(get().items as Map<string, any>) > 1) return

  const tryApplyDrawCameraFit = () => {
    const store = get()
    if (store.cameraViewPreservationDepth > 0 || store.unitReplaceInFlight) return false

    const fittedView = applyLiveDrawCameraFit(store.items as Map<string, any>)
    if (!fittedView) return false

    store.setLastView({
      position: fittedView.position.clone(),
      target: fittedView.target.clone(),
    })
    scheduleLiveDrawCameraFitRetries(store.items as Map<string, any>)
    if (store.shouldFitCameraToItems) {
      store.clearCameraFitToItems()
    }
    return true
  }

  if (tryApplyDrawCameraFit()) return

  requestAnimationFrame(() => {
    if (tryApplyDrawCameraFit()) return
    if (countDrawFitUnits(get().items as Map<string, any>) <= 1) {
      get().requestCameraFitToItems()
    }
  })
}

/**
 * linkedItem: precarga las URLs de textura del item y, cuando hereda texturas,
 * empuja los materiales de zona al renderer.
 */
const preloadDrawnItemTextures = async (ctx: DrawItemPostProcessingContext) => {
  const { get, item, options } = ctx
  if (options?.inheritTexturesFrom?.textures?.length && !item.universal) {
    await preloadSceneTextureUrls(item.textures.map((texture: { url?: string }) => texture.url))
    pushLinkedItemZoneMaterialsToRenderer(get, item.id as AnyID)
  } else if (item.textures?.length && !item.universal) {
    await preloadSceneTextureUrls(item.textures.map((texture: { url?: string }) => texture.url))
  }
}

/**
 * Orquesta el comportamiento post-dibujo específico de cada tipo de item una vez
 * que ya está colocado en la escena. Mantiene el orden original de `drawItem`.
 */
export const runDrawItemPostProcessing = async (ctx: DrawItemPostProcessingContext) => {
  if (isDrawDebugEnabled()) {
    // console.log('[DRAW] runDrawItemPostProcessing', {
    //   id: String(ctx.item?.id),
    //   catalog: ctx.catalogID,
    //   type: ctx.type,
    // })
  }
  reapplyParentSnapAfterDraw(ctx)
  logDrawOverlapDiagnostic(ctx)
  scheduleCameraFit(ctx)
  scheduleUniversalTextureInheritance(ctx)
  await preloadDrawnItemTextures(ctx)
}
